import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { replaceEqualDeep } from "../utils";

interface UseAsyncFetcherResult<T> {
  data: T;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  silentRefetch: () => Promise<void>;
}

export function useAsyncFetcher<T>(
  fetcher: (signal?: AbortSignal) => Promise<T>,
  initialData: T,
  deps: unknown[],
  enabled = true,
  debounceMs = 0
): UseAsyncFetcherResult<T> {
  const [data, setData] = useState<T>(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const prevDataRef = useRef<T>();
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (silent = false, signal?: AbortSignal) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const response = await fetcher(signal);
        if (signal?.aborted) return;
        const newData = replaceEqualDeep(prevDataRef.current, response);
        if (newData !== prevDataRef.current) {
          prevDataRef.current = newData;
          setData(newData);
        }
      } catch (err) {
        if (signal?.aborted) return;
        setError(err as Error);
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    deps
  );

  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    if (!enabled) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (debounceMs > 0) {
      const timer = setTimeout(
        () => fetchData(false, controller.signal),
        debounceMs
      );
      return () => {
        clearTimeout(timer);
        controller.abort();
      };
    }

    fetchData(false, controller.signal);
    return () => controller.abort();
  }, [fetchData, enabled]);

  const refetch = useCallback(() => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return fetchDataRef.current(false, controller.signal);
  }, []);

  const silentRefetch = useCallback(() => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return fetchDataRef.current(true, controller.signal);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      isError: error !== null,
      error,
      refetch,
      silentRefetch,
    }),
    [data, isLoading, error, refetch, silentRefetch]
  );
}
